import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { AnalyticsExportData } from "@/store/analyticsApi";

/**
 * Format currency for display
 */
const formatCurrency = (value: number): string => {
  return `₦${value.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format date for display
 */
const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString("en-NG", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Format status for display
 */
const formatStatus = (status: string): string => {
  return status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Generate and download CSV report
 */
export function generateCSV(data: AnalyticsExportData): void {
  const lines: string[] = [];

  // Header
  lines.push("Sales Analytics Report");
  lines.push(
    `Period: ${formatDate(data.dateRange.startDate)} - ${formatDate(data.dateRange.endDate)}`
  );
  lines.push(`Generated: ${new Date().toLocaleString("en-NG")}`);
  lines.push("");

  // Overview Section
  lines.push("=== SALES OVERVIEW ===");
  lines.push(`Total Revenue,${data.overview.totalRevenue}`);
  lines.push(`Total Orders,${data.overview.totalOrders}`);
  lines.push(`Average Order Value,${data.overview.averageOrderValue}`);
  lines.push(`Completed Orders,${data.overview.completedOrders}`);
  lines.push(`Pending Orders,${data.overview.pendingOrders}`);
  lines.push(`Cancelled Orders,${data.overview.cancelledOrders}`);
  lines.push(
    `Revenue Change vs Previous Period,${data.overview.previousPeriod.revenueChange}%`
  );
  lines.push(
    `Orders Change vs Previous Period,${data.overview.previousPeriod.ordersChange}%`
  );
  lines.push("");

  // Popular Items Section
  lines.push("=== TOP SELLING ITEMS ===");
  lines.push("Product Name,Category,Quantity Sold,Revenue,Order Count");
  data.popularItems.forEach((item) => {
    lines.push(
      `"${item.name}","${item.category}",${item.quantitySold},${item.revenue},${item.orderCount}`
    );
  });
  lines.push("");

  // Category Sales Section
  lines.push("=== SALES BY CATEGORY ===");
  lines.push("Category,Revenue,Items Sold,Order Count,Percentage");
  data.categorySales.forEach((cat) => {
    lines.push(
      `"${cat.name}",${cat.revenue},${cat.itemsSold},${cat.orderCount},${cat.percentage}%`
    );
  });
  lines.push("");

  // Order Status Section
  lines.push("=== ORDER STATUS DISTRIBUTION ===");
  lines.push("Status,Count,Percentage");
  data.orderStatus.forEach((status) => {
    lines.push(
      `"${formatStatus(status.status)}",${status.count},${status.percentage}%`
    );
  });
  lines.push("");

  // Payment Methods Section
  lines.push("=== PAYMENT METHODS ===");
  lines.push("Method,Count,Revenue,Percentage");
  data.paymentMethods.forEach((method) => {
    lines.push(
      `"${method.method}",${method.count},${method.revenue},${method.percentage}%`
    );
  });

  // Create and download file
  const csvContent = lines.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `sales-report-${new Date().toISOString().split("T")[0]}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate and download PDF report
 */
export function generatePDF(data: AnalyticsExportData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("Sales Analytics Report", pageWidth / 2, yPos, { align: "center" });
  yPos += 10;

  // Date Range
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Period: ${formatDate(data.dateRange.startDate)} - ${formatDate(data.dateRange.endDate)}`,
    pageWidth / 2,
    yPos,
    { align: "center" }
  );
  yPos += 6;
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleString("en-NG")}`, pageWidth / 2, yPos, {
    align: "center",
  });
  yPos += 15;

  // Sales Overview Section
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Sales Overview", 14, yPos);
  yPos += 8;

  // Overview metrics in a grid
  const overviewData = [
    ["Total Revenue", formatCurrency(data.overview.totalRevenue)],
    ["Total Orders", data.overview.totalOrders.toString()],
    ["Average Order Value", formatCurrency(data.overview.averageOrderValue)],
    ["Completed Orders", data.overview.completedOrders.toString()],
    ["Pending Orders", data.overview.pendingOrders.toString()],
    ["Cancelled Orders", data.overview.cancelledOrders.toString()],
    [
      "Revenue vs Previous",
      `${data.overview.previousPeriod.revenueChange >= 0 ? "+" : ""}${data.overview.previousPeriod.revenueChange.toFixed(1)}%`,
    ],
    [
      "Orders vs Previous",
      `${data.overview.previousPeriod.ordersChange >= 0 ? "+" : ""}${data.overview.previousPeriod.ordersChange.toFixed(1)}%`,
    ],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [["Metric", "Value"]],
    body: overviewData,
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 14, right: 14 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Top Selling Items Section
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Top Selling Items", 14, yPos);
  yPos += 8;

  const popularItemsData = data.popularItems.slice(0, 15).map((item) => [
    item.name.length > 30 ? item.name.substring(0, 30) + "..." : item.name,
    item.category,
    item.quantitySold.toString(),
    formatCurrency(item.revenue),
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Product", "Category", "Qty Sold", "Revenue"]],
    body: popularItemsData,
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 14, right: 14 },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 40 },
      2: { cellWidth: 25, halign: "right" },
      3: { cellWidth: 40, halign: "right" },
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Category Sales Section
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Sales by Category", 14, yPos);
  yPos += 8;

  const categorySalesData = data.categorySales.map((cat) => [
    cat.name,
    formatCurrency(cat.revenue),
    cat.itemsSold.toString(),
    cat.orderCount.toString(),
    `${cat.percentage}%`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Category", "Revenue", "Items Sold", "Orders", "Share"]],
    body: categorySalesData,
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 14, right: 14 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Order Status Section
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Order Status Distribution", 14, yPos);
  yPos += 8;

  const orderStatusData = data.orderStatus.map((status) => [
    formatStatus(status.status),
    status.count.toString(),
    `${status.percentage}%`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Status", "Count", "Share"]],
    body: orderStatusData,
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 14, right: 14 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Payment Methods Section
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Payment Methods", 14, yPos);
  yPos += 8;

  const paymentMethodsData = data.paymentMethods.map((method) => [
    method.method,
    method.count.toString(),
    formatCurrency(method.revenue),
    `${method.percentage}%`,
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [["Method", "Count", "Revenue", "Share"]],
    body: paymentMethodsData,
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 14, right: 14 },
  });

  // Footer on each page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  // Save the PDF
  doc.save(`sales-report-${new Date().toISOString().split("T")[0]}.pdf`);
}
